import React, { useEffect, useState } from 'react';
import { Box, Typography, MenuItem, Select, FormControl, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Alert, Button, TextField, Switch } from '@mui/material';
import api from '../api/api';
import { DataGridSkeleton } from '../components/LoadingSkeletons';
import ReviewIcon from '@mui/icons-material/RateReview';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { useAuth } from '../context/AuthContext';
import { useFeedback } from '../context/FeedbackContext';
import { useNavigate } from 'react-router-dom';
import { Camera } from '../types';

interface AttendanceResult {
  student_id: number;
  name: string;
  status: string;
  confidence?: number;
  review_status?: string;
}

interface Student {
  id: number;
  name: string;
}

const CLASS_LEVELS = [
  'Pre-Primary 1', 'Pre-Primary 2',
  'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6',
  'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'
];

const Attendance: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedClassLevel, setSelectedClassLevel] = useState<string>('');
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [attendance, setAttendance] = useState<Record<number, AttendanceResult>>({});
  const [loading, setLoading] = useState(false);
  const [cameras, setCameras] = useState<{ id: number; name: string }[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<number | ''>('');
  const { showSuccess, showError } = useFeedback();

  // Always reset date to today on mount
  useEffect(() => {
    setSelectedDate(new Date().toISOString().slice(0, 10));
  }, []);

  // Fetch cameras for the user's school
  useEffect(() => {
    if (user?.school_id) {
      api.get('/cameras/', { params: { school_id: user.school_id } })
        .then(res => {
          setCameras(res.data as Camera[]);
          if ((res.data as any[]).length > 0) setSelectedCamera((res.data as any[])[0].id);
        })
        .catch(() => setCameras([]));
    }
  }, [user]);

  // Fetch students for selected class level
  useEffect(() => {
    if (selectedClassLevel && user?.school_id) {
      api.get('/students/', { params: { school_id: user.school_id, class_level: selectedClassLevel } })
        .then(res => setStudents(res.data as Student[]))
        .catch(() => setStudents([]));
    } else {
      setStudents([]);
    }
  }, [selectedClassLevel, user]);

  // Fetch attendance for selected class level and date
  useEffect(() => {
    if (selectedClassLevel && selectedDate) {
      setLoading(true);
      api.get('/attendance/daily', { params: { class_level: selectedClassLevel, day: selectedDate } })
        .then(res => {
          const att: Record<number, AttendanceResult> = {};
          (res.data as any[]).forEach((r: AttendanceResult) => {
            att[r.student_id] = r;
          });
          setAttendance(att);
          setLoading(false);
        })
        .catch(() => {
          setAttendance({});
          setLoading(false);
        });
    } else {
      setAttendance({});
    }
  }, [selectedClassLevel, selectedDate]);

  const handleToggle = async (student_id: number, checked: boolean) => {
    try {
      const status = checked ? 'present' : 'absent';
      await api.post('/attendance/daily', {
        student_id,
        date: selectedDate,
        status
      });
      setAttendance(prev => ({
        ...prev,
        [student_id]: { ...prev[student_id], status }
      }));
      showSuccess('Attendance updated.');
    } catch (err: any) {
      showError('Failed to update attendance.');
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom>Daily Attendance</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <FormControl sx={{ minWidth: 240, mr: 2 }}>
          <InputLabel>Class Level</InputLabel>
          <Select
            value={selectedClassLevel}
            label="Class Level"
            onChange={e => setSelectedClassLevel(e.target.value)}
          >
            {CLASS_LEVELS.map(level => (
              <MenuItem key={level} value={level}>{level}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Date"
          type="date"
          value={selectedDate}
          onChange={e => setSelectedDate(e.target.value)}
          sx={{ mr: 2 }}
          InputLabelProps={{ shrink: true }}
        />
        <FormControl sx={{ minWidth: 180, mr: 2 }}>
          <InputLabel>Camera</InputLabel>
          <Select
            value={selectedCamera}
            label="Camera"
            onChange={e => setSelectedCamera(Number(e.target.value))}
          >
            {cameras.map(camera => (
              <MenuItem key={camera.id} value={camera.id}>{camera.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedCamera}
          onClick={() => navigate(`/live/${selectedCamera}`)}
        >
          Launch Live View
        </Button>
      </Box>
      {loading ? <DataGridSkeleton /> : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Confidence</TableCell>
                <TableCell>Review Status</TableCell>
                <TableCell>Mark Present</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map(student => {
                const att = attendance[student.id];
                return (
                  <TableRow key={student.id}>
                    <TableCell>{student.name}</TableCell>
                    <TableCell>
                      <Chip label={att?.status || 'absent'} color={att?.status === 'present' ? 'success' : 'error'} size="small" />
                    </TableCell>
                    <TableCell>
                      {att?.confidence !== undefined ? (
                        <Chip
                          icon={att.confidence < 0.8 ? <WarningIcon /> : undefined}
                          label={`${(att.confidence * 100).toFixed(1)}%`}
                          color={att.confidence < 0.8 ? 'warning' : 'success'}
                          variant="outlined"
                          size="small"
                        />
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {att?.review_status ? (
                        <Chip
                          icon={att.review_status === 'confirmed' ? <CheckCircleIcon /> : <ReviewIcon />}
                          label={att.review_status}
                          color={att.review_status === 'confirmed' ? 'success' : 'default'}
                          size="small"
                        />
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={att?.status === 'present'}
                        onChange={e => handleToggle(student.id, e.target.checked)}
                        color="success"
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default Attendance; 